import { useCallback, useEffect, useRef, useState } from 'react';

import cx from '../../utils/cx';
import TEXTS from '../../i18n/texts';

import CollageStrips from './shared/CollageStrips/CollageStrips';
import ProgressCard from './shared/ProgressCard/ProgressCard';
import ShortHeader from './shared/ShortHeader/ShortHeader';
import VerticalCollage from './shared/VerticalCollage/VerticalCollage';
import Welcome from './steps/Welcome/Welcome';
import Choice from './steps/Choice/Choice';
import MultiChoice from './steps/MultiChoice/MultiChoice';
import ShortChoice from './steps/ShortChoice/ShortChoice';
import Final from './steps/Final/Final';
import ShortFinal from './steps/ShortFinal/ShortFinal';

import {
  ANSWER_FEEDBACK_MS,
  FIRST_STEP_NAME,
  LOADER_DURATION,
  LOADER_MORPH_DELAY,
  PROGRESS_STEP_NAMES,
  SHORT_QUESTION_NAMES,
  STEP_INDEX_BY_NAME,
  STEP_TRANSITION_MS,
  STEP_TYPES,
  STEPS,
  THEME,
  USES_SHORT_STEPS,
} from '../../config/quiz';

import { FINAL_COLLAGE, VERTICAL_COLLAGE } from '../../config/collages';

import backIcon from '../../images/quiz/back_icon.svg';

import styles from './Quiz.module.scss';

const HISTORY_KEY = 'quizStep';

const HAS_PROGRESS_HEADER = PROGRESS_STEP_NAMES.length > 0;

const getStep = (name) => STEPS[STEP_INDEX_BY_NAME[name]];

const Quiz = ({ lang }) => {
  const texts = TEXTS[lang];

  const [current, setCurrent] = useState(FIRST_STEP_NAME);
  const [leaving, setLeaving] = useState(null);
  const [direction, setDirection] = useState('forward');
  const [progressRange, setProgressRange] = useState({ from: 0, to: 0 });
  const [isLoaderMorph, setIsLoaderMorph] = useState(false);
  const currentRef = useRef(current);
  const answerIndexRef = useRef(-1);
  const answerTimerRef = useRef(null);

  const step = getStep(current);
  const isHeaderVisible = step.progress !== undefined || Boolean(step.isHeaderLoader);
  const isShortHeaderVisible = SHORT_QUESTION_NAMES.includes(current);

  const changeStep = useCallback((name, dir) => {
    const from = currentRef.current;

    if (from === name) return;

    clearTimeout(answerTimerRef.current);
    currentRef.current = name;
    setLeaving({ name: from, direction: dir, answerIndex: answerIndexRef.current });
    answerIndexRef.current = -1;
    setDirection(dir);
    setCurrent(name);
  }, []);

  useEffect(() => {
    window.history.replaceState({ [HISTORY_KEY]: FIRST_STEP_NAME }, '');

    const handlePopState = (e) => {
      const name = e.state && e.state[HISTORY_KEY];

      if (!name || STEP_INDEX_BY_NAME[name] === undefined) return;

      const dir =
        STEP_INDEX_BY_NAME[name] > STEP_INDEX_BY_NAME[currentRef.current] ? 'forward' : 'back';

      changeStep(name, dir);
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [changeStep]);

  const goNext = useCallback(() => {
    const index = STEP_INDEX_BY_NAME[currentRef.current];
    const next = STEPS[index + 1];

    if (!next) return;

    const state = { [HISTORY_KEY]: next.name };

    if (getStep(currentRef.current).skipHistory) {
      window.history.replaceState(state, '');
    } else {
      window.history.pushState(state, '');
    }

    changeStep(next.name, 'forward');
  }, [changeStep]);

  const handleAnswer = useCallback(
    (index) => {
      if (!ANSWER_FEEDBACK_MS) {
        goNext();

        return;
      }

      answerIndexRef.current = index;
      answerTimerRef.current = setTimeout(goNext, ANSWER_FEEDBACK_MS);
    },
    [goNext],
  );

  useEffect(() => {
    if (!leaving) return undefined;

    const timerId = setTimeout(() => setLeaving(null), STEP_TRANSITION_MS);

    return () => clearTimeout(timerId);
  }, [leaving]);

  useEffect(() => {
    const target = step.progress !== undefined ? step.progress : step.exitProgress;

    if (target === undefined) return;

    setProgressRange((prev) => (prev.to === target ? prev : { from: prev.to, to: target }));
  }, [step]);

  useEffect(() => {
    if (!step.isHeaderLoader) {
      setIsLoaderMorph(false);

      return undefined;
    }

    const morphTimerId = setTimeout(() => setIsLoaderMorph(true), LOADER_MORPH_DELAY);
    const nextTimerId = setTimeout(goNext, LOADER_DURATION);

    return () => {
      clearTimeout(morphTimerId);
      clearTimeout(nextTimerId);
    };
  }, [step, goNext]);

  const renderStep = (name, answerIndex) => {
    const { type } = getStep(name);

    switch (type) {
      case STEP_TYPES.welcome:
        return <Welcome texts={texts} onNext={goNext} />;
      case STEP_TYPES.yesNo:
        return <Choice stepName={name} texts={texts} onNext={goNext} isRow />;
      case STEP_TYPES.singleChoice:
        return <Choice stepName={name} texts={texts} onNext={goNext} />;
      case STEP_TYPES.multiChoice:
        return <MultiChoice stepName={name} texts={texts} onNext={goNext} />;
      case STEP_TYPES.shortYesNo:
      case STEP_TYPES.shortSingleChoice:
        return (
          <ShortChoice
            stepName={name}
            texts={texts}
            onNext={handleAnswer}
            initialChecked={answerIndex}
            isRow={type === STEP_TYPES.shortYesNo}
          />
        );
      case STEP_TYPES.final:
        return <Final texts={texts} />;
      case STEP_TYPES.shortFinal:
        return <ShortFinal texts={texts} />;
      case STEP_TYPES.loader:
      default:
        return <div className={styles.blank} />;
    }
  };

  const headerTexts = texts.header[step.header] || texts.header.ready;
  const dotIndex = PROGRESS_STEP_NAMES.indexOf(current);

  return (
    <div
      className={cx(styles.wrapper, THEME && styles[THEME], {
        [styles.wrapperSharedBg]: USES_SHORT_STEPS,
      })}
    >
      {USES_SHORT_STEPS && (
        <div className={styles.bg}>
          {VERTICAL_COLLAGE ? (
            <VerticalCollage image={VERTICAL_COLLAGE} />
          ) : (
            <>
              <CollageStrips images={FINAL_COLLAGE} rowsCount={8} className={styles.collage} />
              <div className={styles.overlay} />
            </>
          )}
        </div>
      )}

      {HAS_PROGRESS_HEADER && (
        <div
          className={cx(styles.header, { [styles.headerHidden]: !isHeaderVisible })}
          aria-hidden={!isHeaderVisible}
        >
          <div className={cx(styles.nav, { [styles.navHidden]: isLoaderMorph })}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => window.history.back()}
              disabled={!isHeaderVisible || isLoaderMorph}
              aria-label="Back"
            >
              <img src={backIcon} alt="" />
            </button>
            <div className={styles.dots}>
              {PROGRESS_STEP_NAMES.map((name, index) => (
                <span
                  key={name}
                  className={cx(styles.dot, {
                    [styles.dotPast]: index < dotIndex,
                    [styles.dotActive]: index === dotIndex,
                  })}
                />
              ))}
            </div>
            <span className={styles.navSpacer} />
          </div>
          <ProgressCard
            title={headerTexts.title}
            subtitle={headerTexts.subtitle}
            phrases={texts.loader}
            progress={progressRange.to}
            prevProgress={progressRange.from}
            isLoader={isLoaderMorph}
          />
        </div>
      )}

      {USES_SHORT_STEPS && (
        <div
          className={cx(styles.shortHeader, { [styles.headerHidden]: !isShortHeaderVisible })}
          aria-hidden={!isShortHeaderVisible}
        >
          <ShortHeader current={current} />
        </div>
      )}

      <div className={styles.steps}>
        {leaving && (
          <div
            key={`leaving-${leaving.name}`}
            className={cx(styles.step, styles.stepLeaving, {
              [styles.stepExitForward]: leaving.direction === 'forward',
              [styles.stepExitBack]: leaving.direction === 'back',
            })}
          >
            {renderStep(leaving.name, leaving.answerIndex)}
          </div>
        )}
        <div
          key={`current-${current}`}
          className={cx(styles.step, {
            [styles.stepEnterForward]: leaving && direction === 'forward',
            [styles.stepEnterBack]: leaving && direction === 'back',
          })}
        >
          {renderStep(current)}
        </div>
      </div>
    </div>
  );
};

export default Quiz;
